export type AsyncTaskStatus =
  | "WAITING_UPLOAD"
  | "PROCESSING"
  | "READY"
  | "FAILED"
  | "EXPIRED";

export interface AsyncTaskSnapshot {
  readonly operationId: string;
  readonly status: AsyncTaskStatus;
}

export interface PollingPolicy {
  readonly initialDelayMs: number;
  readonly maxDelayMs: number;
  readonly timeoutMs: number;
  readonly backoffFactor?: number;
}

export interface PollAsyncTaskOptions<TSnapshot extends AsyncTaskSnapshot> {
  readonly load: (signal: AbortSignal) => Promise<TSnapshot>;
  readonly policy: PollingPolicy;
  readonly signal?: AbortSignal;
  readonly onProgress?: (snapshot: TSnapshot, attempt: number) => void;
  readonly now?: () => number;
  readonly sleep?: (delayMs: number, signal: AbortSignal) => Promise<void>;
}

export interface PollAsyncTaskResult<TSnapshot extends AsyncTaskSnapshot> {
  readonly outcome: "terminal" | "timeout";
  readonly snapshot: TSnapshot;
  readonly attempts: number;
  readonly elapsedMs: number;
}

export type AsyncTaskScope = "member" | "admin";

export interface RecoverableTask {
  readonly operationId: string;
  readonly scope: AsyncTaskScope;
  readonly operationType: string;
  readonly startedAt: string;
  readonly updatedAt: string;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface AsyncTaskRegistry {
  list(scope?: AsyncTaskScope): readonly RecoverableTask[];
  save(task: RecoverableTask): void;
  remove(operationId: string): void;
  clear(scope?: AsyncTaskScope): void;
}

const TERMINAL_STATUSES = new Set<AsyncTaskStatus>(["READY", "FAILED", "EXPIRED"]);
const REGISTRY_KEY = "carepass.async-tasks.v1";

function validatePolicy(policy: PollingPolicy): Required<PollingPolicy> {
  const factor = policy.backoffFactor ?? 1.5;
  if (!Number.isFinite(policy.initialDelayMs) || policy.initialDelayMs <= 0) {
    throw new Error("Polling initialDelayMs must be positive.");
  }
  if (!Number.isFinite(policy.maxDelayMs) || policy.maxDelayMs < policy.initialDelayMs) {
    throw new Error("Polling maxDelayMs must be greater than or equal to initialDelayMs.");
  }
  if (!Number.isFinite(policy.timeoutMs) || policy.timeoutMs < policy.initialDelayMs) {
    throw new Error("Polling timeoutMs must be greater than or equal to initialDelayMs.");
  }
  if (!Number.isFinite(factor) || factor < 1) {
    throw new Error("Polling backoffFactor must be at least 1.");
  }
  return { ...policy, backoffFactor: factor };
}

function abortedError(): DOMException {
  return new DOMException("The polling operation was aborted.", "AbortError");
}

export function waitFor(delayMs: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) return Promise.reject(abortedError());
  return new Promise((resolve, reject) => {
    const timeout = globalThis.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);
    const onAbort = () => {
      globalThis.clearTimeout(timeout);
      reject(abortedError());
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

export async function pollAsyncTask<TSnapshot extends AsyncTaskSnapshot>(
  options: PollAsyncTaskOptions<TSnapshot>,
): Promise<PollAsyncTaskResult<TSnapshot>> {
  const policy = validatePolicy(options.policy);
  const now = options.now ?? Date.now;
  const sleep = options.sleep ?? waitFor;
  const controller = new AbortController();
  const relayAbort = () => controller.abort(options.signal?.reason);
  options.signal?.addEventListener("abort", relayAbort, { once: true });
  if (options.signal?.aborted) relayAbort();

  const startedAt = now();
  let delayMs = policy.initialDelayMs;
  let attempts = 0;

  try {
    while (true) {
      if (controller.signal.aborted) throw abortedError();
      attempts += 1;
      const snapshot = await options.load(controller.signal);
      options.onProgress?.(snapshot, attempts);
      const elapsedMs = Math.max(0, now() - startedAt);

      if (TERMINAL_STATUSES.has(snapshot.status)) {
        return { outcome: "terminal", snapshot, attempts, elapsedMs };
      }
      if (elapsedMs + delayMs > policy.timeoutMs) {
        return { outcome: "timeout", snapshot, attempts, elapsedMs };
      }

      await sleep(delayMs, controller.signal);
      delayMs = Math.min(policy.maxDelayMs, Math.round(delayMs * policy.backoffFactor));
    }
  } finally {
    options.signal?.removeEventListener("abort", relayAbort);
  }
}

function isRecoverableTask(value: unknown): value is RecoverableTask {
  if (!value || typeof value !== "object") return false;
  const task = value as Partial<RecoverableTask>;
  return typeof task.operationId === "string"
    && task.operationId.length > 0
    && (task.scope === "member" || task.scope === "admin")
    && typeof task.operationType === "string"
    && typeof task.startedAt === "string"
    && typeof task.updatedAt === "string";
}

function readTasks(storage: StorageLike): RecoverableTask[] {
  const serialized = storage.getItem(REGISTRY_KEY);
  if (!serialized) return [];
  try {
    const parsed: unknown = JSON.parse(serialized);
    if (Array.isArray(parsed)) return parsed.filter(isRecoverableTask);
  } catch {
    // Invalid browser state is discarded below.
  }
  storage.removeItem(REGISTRY_KEY);
  return [];
}

function writeTasks(storage: StorageLike, tasks: readonly RecoverableTask[]): void {
  if (tasks.length === 0) storage.removeItem(REGISTRY_KEY);
  else storage.setItem(REGISTRY_KEY, JSON.stringify(tasks));
}

export function createAsyncTaskRegistry(storage: StorageLike): AsyncTaskRegistry {
  return {
    list(scope) {
      const tasks = readTasks(storage);
      return scope ? tasks.filter((task) => task.scope === scope) : tasks;
    },
    save(task) {
      const tasks = readTasks(storage).filter((candidate) => candidate.operationId !== task.operationId);
      writeTasks(storage, [...tasks, task]);
    },
    remove(operationId) {
      writeTasks(storage, readTasks(storage).filter((task) => task.operationId !== operationId));
    },
    clear(scope) {
      if (!scope) {
        storage.removeItem(REGISTRY_KEY);
        return;
      }
      writeTasks(storage, readTasks(storage).filter((task) => task.scope !== scope));
    },
  };
}
